import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

/**
 * Entidad Evento (Boda) — fuente única del núcleo del evento.
 * Ver specs/006-event-entity. Reemplaza la lectura duplicada de nombres/fecha
 * (wedding_invitations + wedding_settings). 1 evento por usuario.
 */
export interface WeddingEvent {
  id: string;
  user_id: string;
  bride_name: string | null;
  groom_name: string | null;
  wedding_date: string | null;
  estilo: string | null;
  total_budget: number;
  created_at: string;
  updated_at: string;
  // 008 — aditivos
  rsvp_deadline: string | null;          // US1: fecha límite de RSVP (instante absoluto)
  country_code: string;                  // i18n: país por defecto (default 'BO')
  whatsapp_message_template: string | null; // US6: plantilla editable
  couple_name_order: string;             // US10: 'bride_first' | 'groom_first'
  coordinator_name: string | null;       // US1 (cierre): coordinador/a de la boda
  coordinator_phone: string | null;      // US1 (cierre): WhatsApp del/la coordinador/a
}

export type WeddingEventInput = Partial<
  Pick<
    WeddingEvent,
    | 'bride_name'
    | 'groom_name'
    | 'wedding_date'
    | 'estilo'
    | 'total_budget'
    | 'rsvp_deadline'
    | 'country_code'
    | 'whatsapp_message_template'
    | 'couple_name_order'
    | 'coordinator_name'
    | 'coordinator_phone'
  >
>;

export const EVENT_QUERY_KEY = 'event';

/** Lee el evento (boda) del usuario actual. Devuelve null si aún no existe. */
export const useEvent = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: [EVENT_QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('weddings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return (data as WeddingEvent | null) ?? null;
    },
    enabled: !!user,
  });
};

/** Crea o actualiza el evento del usuario (1 por usuario). */
export const useUpsertEvent = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: WeddingEventInput) => {
      if (!user) throw new Error('No autenticado');

      const { data: existing } = await supabase
        .from('weddings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      let result: WeddingEvent;
      if (existing) {
        const { data, error } = await supabase
          .from('weddings')
          .update(input)
          .eq('user_id', user.id)
          .select()
          .single();
        if (error) throw error;
        result = data as WeddingEvent;
      } else {
        const { data, error } = await supabase
          .from('weddings')
          .insert({ ...input, user_id: user.id })
          .select()
          .single();
        if (error) throw error;
        result = data as WeddingEvent;
      }

      // 004: mantener el "snapshot público" de la invitación en sync. La invitación
      // publicada es el artículo que ve el invitado (anónimo, sin acceso a `weddings`).
      // Al editar nombres/fecha en el evento, se replican a las columnas de la
      // invitación para que /i/:slug muestre datos vivos sin exponer `weddings`.
      const nameOrDate =
        'bride_name' in input ||
        'groom_name' in input ||
        'wedding_date' in input ||
        'couple_name_order' in input; // 008 · US10
      if (nameOrDate) {
        const snapshot: Record<string, unknown> = {};
        if (input.bride_name !== undefined) snapshot.bride_name = input.bride_name ?? '';
        if (input.groom_name !== undefined) snapshot.groom_name = input.groom_name ?? '';
        if (input.wedding_date !== undefined && input.wedding_date) {
          snapshot.wedding_date = input.wedding_date;
        }
        if (input.couple_name_order !== undefined) {
          snapshot.couple_name_order = input.couple_name_order; // 008 · US10
        }
        if (Object.keys(snapshot).length > 0) {
          await supabase.from('wedding_invitations').update(snapshot).eq('user_id', user.id);
        }
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVENT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['wedding-settings'] });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast({ title: 'Boda actualizada' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: (error as Error).message, variant: 'destructive' });
    },
  });
};

/**
 * Garantiza que exista el evento del usuario y devuelve su id.
 * Útil para hooks que necesitan `event_id` al crear filas (presupuesto, tareas, invitaciones).
 */
export async function ensureEventId(userId: string): Promise<string> {
  const { data: existing, error: readErr } = await supabase
    .from('weddings')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  if (readErr) throw readErr;
  if (existing) return existing.id;

  const { data, error } = await supabase
    .from('weddings')
    .insert({ user_id: userId })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}
